/**
 * One search result row.
 *
 * Renders: entity-type icon, title, one-line snippet (with `<mark>` around
 * lexical matches the server emitted), parent_context, updated_at.
 *
 * Workspace rows render plain title + snippet — NO status badge
 * (workspace `status` was cut from v1).
 *
 * Session rows show the server-emitted snippet (title/description match for
 * `session`; message content for `session_message`). Parent context shows the
 * workspace name + "yourself" attribution. Click navigates via the
 * server-emitted route + route_params (the `:name` placeholder syntax
 * is router-agnostic; `substituteRoute` produces the literal URL the router
 * accepts).
 */

import { useNavigate } from 'react-router-dom';
import { cn } from '@localagent/ui/lib/utils';
import type { EntityType, SearchResult } from '@localagent/sdk';
import { ENTITY_TYPE_LABELS, type EntityTypeLabel } from './entity-types';

function entityTypeLabel(entityType: EntityType): EntityTypeLabel {
  const label = ENTITY_TYPE_LABELS[entityType];
  if (!label) {
    throw new Error(`search result entity_type "${entityType}" has no client label`);
  }
  return label;
}

/**
 * Substitute `:name` placeholders in `template` with URL-encoded values
 * from `params`. The wire shape (`:name`) is router-agnostic by design;
 * this helper produces the literal URL string the
 * app-shell router accepts as input to `navigate()`.
 *
 * Fail-fast (per `.claude/rules/no-fallbacks.md`): if a placeholder has no
 * matching key in `params`, throw. The result payload is broken at that
 * point — silently substituting the empty string would route to the wrong
 * URL and hide the bug.
 */
export function substituteRoute(template: string, params: Record<string, string>): string {
  return template.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, key: string) => {
    const v = params[key];
    if (v == null) {
      throw new Error(`route_params missing "${key}" for "${template}"`);
    }
    return encodeURIComponent(v);
  });
}

/**
 * Build the navigation URL for a result from its server-emitted route +
 * route_params. `session_message` results carry the owning `session_id` in
 * route_params and deep-link to that session's chat view; `session`
 * (title/description) results deep-link to the bare session route. (The old
 * `#event=` turn anchor was retired with the metadata-only session doc —
 * search ADR-001.)
 */
export function resultUrl(result: SearchResult): string {
  if (result.entity_type === 'account_artifact') {
    if (!result.entity_id) {
      throw new Error('account_artifact hit missing entity_id');
    }
    return `/artifacts/${encodeURIComponent(result.entity_id)}`;
  }
  return substituteRoute(result.route, result.route_params);
}

/** Format ISO timestamp into a short relative-ish label. */
function formatUpdatedAt(ts: string): string {
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return ts;
  }
}

/**
 * Render the snippet with `<mark>` highlighting preserved. The server emits
 * a sanitized snippet with `<mark>...</mark>` around lexical hits; we parse
 * those into React nodes rather than dangerouslySetInnerHTML.
 */
function renderSnippet(snippet: string): React.ReactNode {
  if (!snippet) return null;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = /<mark>([\s\S]*?)<\/mark>/g;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(snippet)) !== null) {
    if (match.index > lastIndex) {
      parts.push(snippet.slice(lastIndex, match.index));
    }
    parts.push(
      <mark key={`m-${key++}`} className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-700">
        {match[1]}
      </mark>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < snippet.length) {
    parts.push(snippet.slice(lastIndex));
  }
  return parts.length > 0 ? parts : snippet;
}

export interface ResultRowProps {
  result: SearchResult;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function ResultRow({ result, isSelected, onSelect }: ResultRowProps) {
  const navigate = useNavigate();

  const onClick = () => {
    onSelect?.();
    navigate(resultUrl(result));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="option"
      aria-selected={isSelected ?? false}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      data-testid={`search-result-${result.entity_type}-${result.entity_id}`}
      data-entity-type={result.entity_type}
      data-entity-id={result.entity_id}
      className={cn(
        'cursor-pointer rounded-md p-3 transition-colors',
        isSelected
          ? 'bg-accent'
          : 'hover:bg-accent',
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className="text-lg leading-none"
          title={entityTypeLabel(result.entity_type).singular}
          aria-label={entityTypeLabel(result.entity_type).singular}
        >
          {entityTypeLabel(result.entity_type).icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="truncate font-medium" data-testid="result-title">
              {result.title}
            </h3>
            <span className="shrink-0 text-xs text-muted-foreground" data-testid="result-updated-at">
              {formatUpdatedAt(result.updated_at)}
            </span>
          </div>
          {result.snippet && (
            <p
              className="mt-0.5 line-clamp-1 text-sm text-muted-foreground"
              data-testid="result-snippet"
            >
              {renderSnippet(result.snippet)}
            </p>
          )}
          {result.parent_context && (
            <p
              className="mt-0.5 text-xs text-muted-foreground"
              data-testid="result-parent-context"
            >
              {result.parent_context}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
